import {
  isObjectRecord,
  toArrayIndexPathSegment,
  toObjectKeyPathSegment,
  type ObjectRecord,
  type PathSegments
} from "./esim-lookup.ordering.common";
import { OrderingLimitExceededError, ProfileOrderIndex } from "./esim-lookup.ordering.profile-order-index";

type RootHolder = { root: unknown };
type SortParentContainer = ObjectRecord | unknown[] | RootHolder;
type SortParentKey = string | number | "root";
type ResolvedOrderIndexByKey = Map<string, number | undefined>;
type SortFrame = {
  value: unknown;
  pathSegments: PathSegments;
  parent: SortParentContainer;
  parentKey: SortParentKey;
};

export class TemplateOrderer {
  private nodeVisits = 0;

  // Uses precomputed profile key-order indexes when sorting template objects.
  public constructor(
    private readonly profileOrderIndex: ProfileOrderIndex,
    private readonly maxNodeVisits: number,
    private readonly maxObjectKeysPerObject: number
  ) {}

  // Returns a deep-sorted copy of the template based on profile ordering.
  public order<T>(templateJson: T): T {
    this.nodeVisits = 0;
    const rootHolder: RootHolder = { root: undefined };
    const stack: SortFrame[] = [{ value: templateJson, pathSegments: [], parent: rootHolder, parentKey: "root" }];

    while (stack.length > 0) {
      this.assertNodeVisitCapacity();
      this.nodeVisits += 1;

      const frame = stack.pop();
      if (!frame) {
        continue;
      }

      const { value, pathSegments, parent, parentKey } = frame;

      if (Array.isArray(value)) {
        const sortedArray: unknown[] = new Array(value.length);
        this.assignSortedValue(parent, parentKey, sortedArray);

        for (let index = value.length - 1; index >= 0; index -= 1) {
          this.assertStackCapacity(stack.length);
          stack.push({
            value: value[index],
            pathSegments: [...pathSegments, toArrayIndexPathSegment(index)],
            parent: sortedArray,
            parentKey: index
          });
        }

        continue;
      }

      if (!isObjectRecord(value)) {
        this.assignSortedValue(parent, parentKey, value);
        continue;
      }

      const keys = Object.keys(value);
      this.assertObjectKeysPerObjectCapacity(keys.length);
      const resolvedOrderIndexByKey = this.resolveOrderIndexesForKeys(keys, pathSegments);
      keys.sort((leftKey, rightKey) => this.compareKeys(leftKey, rightKey, resolvedOrderIndexByKey));

      const sortedObject: ObjectRecord = {};
      this.assignSortedValue(parent, parentKey, sortedObject);

      for (let keyIndex = keys.length - 1; keyIndex >= 0; keyIndex -= 1) {
        const key = keys[keyIndex];
        this.assertStackCapacity(stack.length);
        stack.push({
          value: value[key],
          pathSegments: [...pathSegments, toObjectKeyPathSegment(key)],
          parent: sortedObject,
          parentKey: key
        });
      }
    }

    return rootHolder.root as T;
  }

  // Resolves the profile order index for each key once before sorting comparisons.
  private resolveOrderIndexesForKeys(keys: string[], pathSegments: PathSegments): ResolvedOrderIndexByKey {
    const resolvedOrderIndexByKey: ResolvedOrderIndexByKey = new Map<string, number | undefined>();

    for (const key of keys) {
      resolvedOrderIndexByKey.set(key, this.profileOrderIndex.resolveOrderIndex([...pathSegments, toObjectKeyPathSegment(key)]));
    }

    return resolvedOrderIndexByKey;
  }

  // Compares keys by resolved profile index and falls back to alphabetical order.
  private compareKeys(leftKey: string, rightKey: string, resolvedOrderIndexByKey: ResolvedOrderIndexByKey): number {
    const leftIndex = resolvedOrderIndexByKey.get(leftKey);
    const rightIndex = resolvedOrderIndexByKey.get(rightKey);

    if (leftIndex !== undefined && rightIndex !== undefined) {
      return leftIndex - rightIndex;
    }

    if (leftIndex !== undefined) {
      return -1;
    }

    if (rightIndex !== undefined) {
      return 1;
    }

    // Keep deterministic output when profile does not define order for either key.
    return leftKey.localeCompare(rightKey);
  }

  // Writes sorted node results to either root, array slot, or object property.
  private assignSortedValue(parent: SortParentContainer, parentKey: SortParentKey, value: unknown): void {
    if (parentKey === "root") {
      (parent as RootHolder).root = value;
      return;
    }

    if (typeof parentKey === "number") {
      (parent as unknown[])[parentKey] = value;
      return;
    }

    (parent as ObjectRecord)[parentKey] = value;
  }

  private assertNodeVisitCapacity(): void {
    if (this.nodeVisits >= this.maxNodeVisits) {
      throw new OrderingLimitExceededError(`Exceeded maximum node visits (${this.maxNodeVisits}) while ordering template.`);
    }
  }

  private assertStackCapacity(stackLength: number): void {
    if (stackLength >= this.maxNodeVisits) {
      throw new OrderingLimitExceededError(`Exceeded maximum queued nodes (${this.maxNodeVisits}) while ordering template.`);
    }
  }

  private assertObjectKeysPerObjectCapacity(objectKeyCount: number): void {
    if (objectKeyCount > this.maxObjectKeysPerObject) {
      throw new OrderingLimitExceededError(
        `Exceeded maximum object keys per object (${this.maxObjectKeysPerObject}) while ordering template.`
      );
    }
  }
}
